import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {useCredentials} from '../../lib/user';
import {getErrorMessage} from '../../lib/helpers';
import Button from '../../components/Button';
import Icon from '../../components/Icon';

import SubConfiguration from './SubConfiguration';

// The shared answers a saved receiving question offers right now, each of which can be removed. A removal is made on
// the server straight away rather than with the form save, since it applies to every form the answer is shared to.
//  - onConfirm asks the card to confirm an action before it runs.
function OfferedAnswers({question, onConfirm}) {
  const credentials = useCredentials();
  const [open, setOpen] = useState(false);
  // null while loading, false when the list could not be loaded.
  const [options, setOptions] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  // A value submitted on several forms or rows is offered once, so it is listed once and removed everywhere at once.
  const load = useCallback(() => {
    return api.getSharedOptions(question.id, credentials)
      .then(response => setOptions(groupByValue(response.sharedOptions || [])))
      .catch(() => setOptions(false));
  }, [question.id, credentials]);

  useEffect(() => {
    if (open) {
      setOptions(null);
      load();
    }
  }, [open, load]);

  const remove = async option => {
    setRemovingId(option.id);

    try {
      for (const id of option.ids) {
        await api.removeSharedOption(question.id, id, credentials);
      }
      NotificationManager.success(`${option.value} is no longer offered.`);
      await load();
    }
    catch (error) {
      NotificationManager.error(await getErrorMessage(error, 'Unable to remove this answer. Please try again.'));
      // Some of a grouped answer's copies may have gone before the failure, so the list is shown as it now stands.
      await load();
    }
    finally {
      setRemovingId(null);
    }
  };

  const handleRemove = option => {
    onConfirm({
      title: 'Remove this answer?',
      description: `${option.value} will no longer be offered on any form it is shared to. The submitted answer is not changed.`,
      ok: 'Remove',
      onOk: () => remove(option)
    });
  };

  return (
    <div>
      <Toggle onClick={() => setOpen(!open)}>
        <Icon name="chevron-right" style={{transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}} />
        &nbsp;
        Offered answers
      </Toggle>
      {open && (
        <>
          <Help>Removing an answer stops offering it on every form it is shared to. The submitted answer is not changed.</Help>
          <SubConfiguration aria-live="polite">
            {options === null && (
              <SubConfiguration.Item>Loading offered answers...</SubConfiguration.Item>
            )}
            {options === false && (
              <SubConfiguration.Item>The offered answers could not be loaded. Reload the page to try again.</SubConfiguration.Item>
            )}
            {options && options.length === 0 && (
              <SubConfiguration.Item>No shared answers are offered right now.</SubConfiguration.Item>
            )}
            {(options || []).map(option => (
              <SubConfiguration.Item key={option.id}>
                <Row>
                  <Value>{option.value}</Value>
                  <Button
                    tertiary
                    disabled={removingId !== null}
                    aria-label={`Remove ${option.value}`}
                    onClick={() => handleRemove(option)}>
                    Remove
                  </Button>
                </Row>
              </SubConfiguration.Item>
            ))}
          </SubConfiguration>
        </>
      )}
    </div>
  );
}

const Toggle = styled.div`
  cursor: pointer;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
  user-select: none;
`;

const Help = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #747474;
  margin: 0.25rem 0 0.5rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  white-space: normal;
`;

const Value = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

function groupByValue(options) {
  const byValue = new Map();
  options.forEach(option => {
    const group = byValue.get(option.value);
    if (group) {
      group.ids.push(option.id);
    }
    else {
      byValue.set(option.value, {id: option.id, value: option.value, ids: [option.id]});
    }
  });
  return [...byValue.values()];
}

export default OfferedAnswers;
